/**
 * Insurance and Coverage Service
 * Handles API calls for patient insurance coverage management
 */

import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import {
  InsuranceCoverage,
  CreateInsuranceCoverageRequest,
  CreateInsuranceCoverageResponse,
  ListInsuranceCoveragesResponse,
  LogVerificationRequest,
  CoverageType,
  COBOrder,
  CoverageStatus,
  MedicareType,
  VerificationStatus
} from '../../dtos/patientmr/insurance.dto';

// Re-export types and enums for convenience
export type {
  InsuranceCoverage,
  CreateInsuranceCoverageRequest,
  CreateInsuranceCoverageResponse,
  ListInsuranceCoveragesResponse,
  LogVerificationRequest
};

export {
  CoverageType,
  COBOrder,
  CoverageStatus,
  MedicareType,
  VerificationStatus
};

/**
 * Insurance Coverage Service
 */
export const InsuranceService = {
  /**
   * List all insurance coverages for a patient
   * @param patientId - Patient identifier
   * @returns List of insurance coverages
   */
  listCoverages: async (patientId: string): Promise<ListInsuranceCoveragesResponse> => {
    const response: ApiResponse<ListInsuranceCoveragesResponse> = await apiClient.get(
      `/patientmr/${patientId}/insurance`
    );

    if (response.success && response.data) {
      // Sort coverages: Primary first, then by effective date (most recent first)
      const sortedCoverages = (response.data.coverages || []).sort((a, b) => {
        // Primary coverage always first
        if (a.cobOrder === COBOrder.PRIMARY && b.cobOrder !== COBOrder.PRIMARY) return -1;
        if (a.cobOrder !== COBOrder.PRIMARY && b.cobOrder === COBOrder.PRIMARY) return 1;
        
        // Then by effective date (most recent first)
        const dateA = new Date(a.effectiveDate).getTime();
        const dateB = new Date(b.effectiveDate).getTime();
        return dateB - dateA;
      });

      return {
        ...response.data,
        coverages: sortedCoverages
      };
    }

    throw new Error(response.error?.message || 'Failed to list insurance coverages');
  },

  /**
   * Create a new insurance coverage
   * @param patientId - Patient identifier
   * @param coverageData - Coverage data
   * @returns Created coverage details
   */
  createCoverage: async (
    patientId: string,
    coverageData: CreateInsuranceCoverageRequest
  ): Promise<CreateInsuranceCoverageResponse> => {
    const response: ApiResponse<CreateInsuranceCoverageResponse> = await apiClient.post(
      `/patientmr/${patientId}/insurance`,
      coverageData
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to create insurance coverage');
  },

  /**
   * Update an existing insurance coverage
   * @param patientId - Patient identifier
   * @param coverageId - Coverage identifier
   * @param coverageData - Updated coverage data
   * @returns Updated coverage details
   */
  updateCoverage: async (
    patientId: string,
    coverageId: string,
    coverageData: Partial<CreateInsuranceCoverageRequest>
  ): Promise<InsuranceCoverage> => {
    const response: ApiResponse<InsuranceCoverage> = await apiClient.put(
      `/patientmr/${patientId}/insurance/${coverageId}`,
      coverageData
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to update insurance coverage');
  },

  /**
   * Terminate a coverage: set its end date (there is no delete; REQ-INS-04, Phase 4 D1).
   * The server audits a terminationDate-only update as "terminate".
   */
  terminateCoverage: async (
    patientId: string,
    coverageId: string,
    terminationDate: string
  ): Promise<InsuranceCoverage> => {
    const response: ApiResponse<InsuranceCoverage> = await apiClient.put(
      `/patientmr/${patientId}/insurance/${coverageId}`,
      { terminationDate }
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to terminate insurance coverage');
  },

  /**
   * Log a manual eligibility verification (REQ-INS-10). Append-only.
   */
  logVerification: async (
    patientId: string,
    coverageId: string,
    entry: LogVerificationRequest
  ): Promise<InsuranceCoverage> => {
    const response: ApiResponse<InsuranceCoverage> = await apiClient.post(
      `/patientmr/${patientId}/insurance/${coverageId}/verifications`,
      entry
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to log verification');
  },

  /**
   * Format date for display
   * @param dateString - Date string
   * @returns Formatted date string
   */
  formatDate: (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  },

  /**
   * Get status badge styles
   * @param status - Coverage status
   * @returns CSS classes for badge
   */
  getStatusBadgeStyles: (status: CoverageStatus): string => {
    switch (status) {
      case CoverageStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case CoverageStatus.INACTIVE:
        return 'bg-gray-100 text-gray-800';
      case CoverageStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case CoverageStatus.EXPIRED:
      case CoverageStatus.TERMINATED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  },

  /**
   * Get verification status badge styles
   * @param status - Verification status
   * @returns CSS classes for badge
   */
  getVerificationStatusBadgeStyles: (status?: VerificationStatus): string => {
    switch (status) {
      case VerificationStatus.VERIFIED:
        return 'bg-green-100 text-green-800';
      case VerificationStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case VerificationStatus.FAILED:
        return 'bg-red-100 text-red-800';
      case VerificationStatus.NOT_VERIFIED:
      default:
        return 'bg-gray-100 text-gray-800';
    }
  },

  /**
   * Get COB order badge styles
   * @param order - COB order
   * @returns CSS classes for badge
   */
  getCOBOrderBadgeStyles: (order: COBOrder): string => {
    switch (order) {
      case COBOrder.PRIMARY:
        return 'bg-blue-100 text-blue-800 font-semibold';
      case COBOrder.SECONDARY:
        return 'bg-purple-100 text-purple-800';
      case COBOrder.TERTIARY:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  },

  /**
   * Check if coverage is expired
   * @param coverage - Insurance coverage
   * @returns True if coverage is expired
   */
  isCoverageExpired: (coverage: InsuranceCoverage): boolean =>
    coverage.status === CoverageStatus.EXPIRED || coverage.status === CoverageStatus.TERMINATED,

  /**
   * True when the coverage is in effect today. Status is derived by the server from the dates in
   * the agency timezone (REQ-INS-04), so the client never recomputes it.
   */
  isCoverageActive: (coverage: InsuranceCoverage): boolean => coverage.status === CoverageStatus.ACTIVE
};

export default InsuranceService;
